import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
interface StepItem {
  number: string;
  title: string;
  description: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  deliverables: string[];
}
const STEPS: StepItem[] = [{
  number: "01",
  title: "In-home consultation",
  description: "We visit your home to assess your kitchen space, discuss how you live and cook, evaluate structural layout possibilities, and establish realistic project goals and budgets.",
  deliverables: ["Lifestyle and layout audit", "Initial budget review", "Feasibility evaluation"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_consultation_planning.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_consultation_planning.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_consultation_planning.png-wvc-sizes",
    alt: "Homeowner and renovation specialist reviewing architectural kitchen plans at an island"
  }
}, {
  number: "02",
  title: "Design and material planning",
  description: "Our team translates your ideas into detailed 3D renderings and comprehensive blueprints. Together, we select cabinetry, countertops, fixtures, appliances, and tile selections before any work begins.",
  deliverables: ["Detailed 3D layout renderings", "Cabinetry and fixture schedules", "Itemized trade estimate"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_design_materials.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_design_materials.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_design_materials.png-wvc-sizes",
    alt: "Samples of natural stone quartz countertops, custom shaker cabinet swatches, and brushed brass hardware"
  }
}, {
  number: "03",
  title: "Permitting and documentation",
  description: "We prepare all necessary technical documentation and secure county and municipal building permits. Every plumbing, electrical, and structural change is fully filed and approved before tools arrive.",
  deliverables: ["Permit application management", "Code compliance filings", "Final trade scheduling"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_permitting_structural.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_permitting_structural.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_permitting_structural.png-wvc-sizes",
    alt: "Architectural blueprints and approved municipal building permits spread out over a wooden work surface"
  }
}, {
  number: "04",
  title: "Construction and trades",
  description: "Our project managers oversee clean demolition, framing adjustments, mechanical rough-ins, and artisan cabinet installation. We maintain dust barriers and conduct daily site clean-ups throughout.",
  deliverables: ["Dedicated site management", "Weekly milestone reports", "HEPA dust containment"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_construction_trades.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_construction_trades.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_construction_trades.png-wvc-sizes",
    alt: "Skilled carpenter installing solid wood kitchen island cabinetry and recessed overhead lighting"
  }
}, {
  number: "05",
  title: "Final walkthrough and sign-off",
  description: "We review every drawer glide, fixture seal, and surface finish side by side with you. We close out all final city inspections, provide manufacturer warranties, and ensure complete satisfaction.",
  deliverables: ["Formal punch-list resolution", "Municipal final inspection certificate", "Warranty documentation packet"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_final_walkthrough.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_final_walkthrough.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_final_walkthrough.png-wvc-sizes",
    alt: "Completed bright transitional kitchen with marble island, brass pendant lights, and polished hardware"
  }
}];
export default function KitchenProcess() {
  return <section id="kitchenremodelingprocess" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <p className="text-xs uppercase font-mono tracking-wider text-muted-foreground mb-3">
            Step-by-step roadmap
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-normal tracking-tight text-foreground">
            Our kitchen remodeling process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From your earliest ideas through to the final polish, our structured five-stage method keeps budgets predictable, schedules firm, and craftsmanship accountable.
          </p>
        </div>

        {/* 5-Step Process List */}
        <div className="space-y-16 md:space-y-24">
          {STEPS.map((step, index) => {
          const isEven = index % 2 === 1;
          return <div key={step.number} data-index={index} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${isEven ? "lg:flex-row-reverse" : ""}`}>
                {/* Visual block */}
                <div className={`lg:col-span-6 ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative overflow-hidden rounded-xl bg-muted border border-border shadow-sm group">
                    <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-72 sm:h-84 md:h-96 object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-xs px-3 py-1 rounded text-xs font-mono text-foreground border border-border/80">
                      Phase {step.number}
                    </div>
                  </div>
                </div>

                {/* Text and deliverables block */}
                <div className={`lg:col-span-6 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="max-w-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl sm:text-4xl font-mono font-medium text-primary">
                        {step.number}
                      </span>
                      <div className="h-px bg-border flex-1" />
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-serif text-foreground font-normal tracking-tight">
                      {step.title}
                    </h3>

                    <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>

                    <div className="mt-6 pt-6 border-t border-border">
                      <h4 className="text-xs uppercase font-mono tracking-wider text-muted-foreground mb-3">
                        Key deliverables
                      </h4>
                      <ul className="space-y-2">
                        {step.deliverables.map((item, itemIdx) => <li key={itemIdx} className="flex items-center gap-2.5 text-sm text-foreground">
                            <CheckCircle2 className="size-4 text-primary shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Bottom CTA Card */}
        <div className="mt-20 md:mt-28 p-8 md:p-12 rounded-xl bg-card border border-border shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-2xl font-serif font-normal text-card-foreground">
              Ready to begin step one?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Schedule your in-home kitchen design consultation with our renovation specialists today.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/ContactRequestAnEstimatePage">
              Request an estimate
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}